/**
 * Numbers over time, for charts: how much is owed to you week by week, and how much came in
 * each month. All from the same ledger rules, so the last point always matches the totals.
 */
import type { Ledger } from "./ledger.ts";
import type { Micro } from "../money.ts";
import type { GroupState, IsoDate } from "../types.ts";

export interface TrendPoint {
  date: IsoDate;
  valueMicro: Micro;
}

function iso(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Saturdays from the week containing `from` up to the week containing `to` (the last is `to`). */
export function weekEnds(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  const d = new Date(`${from}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + ((6 - d.getUTCDay() + 7) % 7));
  while (iso(d) < to) {
    out.push(iso(d));
    d.setUTCDate(d.getUTCDate() + 7);
  }
  out.push(to);
  return out;
}

/** Points for a chart from `from` to `to`: every day for short spans, week ends for longer. */
export function trendDates(from: IsoDate, to: IsoDate, dailyUpTo = 45): IsoDate[] {
  const days = Math.round(
    (Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000,
  );
  if (days > dailyUpTo) return weekEnds(from, to);
  const out: IsoDate[] = [];
  const d = new Date(`${from}T12:00:00Z`);
  for (let i = 0; i <= Math.max(0, days); i++) {
    out.push(iso(d));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** Day-by-day changes to what others owe you: their shares, minus what they paid or fronted. */
function owedDeltas(state: GroupState, ledger: Ledger, only?: string): Map<IsoDate, Micro> {
  const me = state.settings.ownerPersonId;
  const counts = (pid: string) => pid !== me && !!state.people[pid] && (!only || pid === only);
  const deltas = new Map<IsoDate, Micro>();
  const add = (d: IsoDate, v: Micro) => deltas.set(d, (deltas.get(d) ?? 0) + v);
  for (const c of ledger.items) {
    for (const pid in c.shares) if (counts(pid)) add(c.item.date, c.shares[pid]!);
    const by = c.item.paidBy;
    if (by && counts(by)) add(c.item.date, -c.totalMicro);
  }
  for (const id of state.paymentOrder) {
    const p = state.payments[id];
    if (p && counts(p.personId)) add(p.at.slice(0, 10), -p.cents * 10_000);
  }
  return deltas;
}

/** The first day anything happened in the group, or null when it's empty. */
export function firstActivity(state: GroupState, ledger: Ledger): IsoDate | null {
  const dates = [...owedDeltas(state, ledger).keys()].sort();
  return dates[0] ?? null;
}

/**
 * Net owed to you at each of the given dates (others' balances added up; negative when you
 * owe more than you're owed).
 */
export function owedTrend(
  state: GroupState,
  ledger: Ledger,
  at: readonly IsoDate[],
  /** Just this person's balance. */
  personId?: string,
): TrendPoint[] {
  const deltas = [...owedDeltas(state, ledger, personId).entries()].sort((a, b) =>
    a[0] < b[0] ? -1 : 1,
  );
  const out: TrendPoint[] = [];
  let i = 0;
  let running = 0;
  for (const date of at) {
    while (i < deltas.length && deltas[i]![0] <= date) running += deltas[i++]![1];
    out.push({ date, valueMicro: running });
  }
  return out;
}

/** Money that came in (positive payments from others), by month "YYYY-MM". */
export function collectedByMonth(state: GroupState): Map<string, Micro> {
  const me = state.settings.ownerPersonId;
  const out = new Map<string, Micro>();
  for (const id of state.paymentOrder) {
    const p = state.payments[id];
    if (!p || p.cents <= 0 || p.personId === me) continue;
    const m = p.at.slice(0, 7);
    out.set(m, (out.get(m) ?? 0) + p.cents * 10_000);
  }
  return out;
}

/** Every month "YYYY-MM" from `from` through `to`, inclusive. */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let [y, m] = from.split("-").map(Number) as [number, number];
  const [ty, tm] = to.split("-").map(Number) as [number, number];
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}
